/**
 * Auth Hook and Context
 *
 * Manages authentication state and provides auth methods.
 * Tokens are stored in HttpOnly cookies by the backend —
 * the frontend only tracks user/household metadata in localStorage.
 */

import { setAuthFailureCallback, setTokenRefreshCallback } from '@/shared/services/api';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { authService, clearStoredAuth, getStoredAuthData, storeAuthData } from '../services/auth-service';
import type { AuthHousehold, AuthStatus, AuthUser, InvitedSignupRequest, LoginRequest, SignupRequest } from '../types';

// =============================================================================
// Context Types
// =============================================================================

interface AuthContextValue {
  // State
  status: AuthStatus;
  user: AuthUser | null;
  household: AuthHousehold | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Computed
  isOwner: boolean;
  userId: string | null;
  householdId: string | null;

  // Actions
  login: (request: LoginRequest) => Promise<void>;
  signup: (request: SignupRequest) => Promise<{ message: string }>;
  signupInvited: (request: InvitedSignupRequest) => Promise<void>;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<boolean>;
  updateUser: (partial: Partial<AuthUser>) => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// =============================================================================
// Token Refresh Logic
// =============================================================================

// Track if a refresh is in progress to avoid concurrent refreshes
let refreshPromise: Promise<boolean> | null = null;

async function performTokenRefresh(): Promise<boolean> {
  // If a refresh is already in flight, share its result (mutex)
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = authService.refresh().then(
    (response) => {
      storeAuthData(response);
      return true;
    },
    () => {
      clearStoredAuth();
      return false;
    }
  );

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

// =============================================================================
// Auth Provider
// =============================================================================

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [household, setHousehold] = useState<AuthHousehold | null>(null);

  // Initialize from stored data and register refresh callback
  useEffect(() => {
    // Register callbacks FIRST so they're available before any refresh attempts
    setTokenRefreshCallback(performTokenRefresh);
    setAuthFailureCallback(() => {
      clearStoredAuth();
      setUser(null);
      setHousehold(null);
      setStatus('unauthenticated');
    });

    const storedData = getStoredAuthData();
    if (storedData) {
      setUser(storedData.user);
      setHousehold(storedData.household);
      // Optimistically set authenticated, but validate cookie in background
      setStatus('authenticated');

      // Use mutex-protected refresh (prevents double-fire from StrictMode
      // and races with API client 401-retry)
      let cancelled = false;
      performTokenRefresh().then((success) => {
        if (cancelled) return;
        if (success) {
          const data = getStoredAuthData();
          if (data) {
            setUser(data.user);
            setHousehold(data.household);
          }
        } else {
          setUser(null);
          setHousehold(null);
          setStatus('unauthenticated');
        }
      });

      return () => {
        cancelled = true;
      };
    } else {
      setStatus('unauthenticated');
    }
  }, []);

  // Refresh auth - returns true if successful
  const refreshAuth = useCallback(async (): Promise<boolean> => {
    const success = await performTokenRefresh();

    if (success) {
      const storedData = getStoredAuthData();
      if (storedData) {
        setUser(storedData.user);
        setHousehold(storedData.household);
        setStatus('authenticated');
      }
    } else {
      setUser(null);
      setHousehold(null);
      setStatus('unauthenticated');
    }

    return success;
  }, []);

  // Update user fields (e.g. after avatar upload/delete)
  const updateUser = useCallback((partial: Partial<AuthUser>) => {
    setUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, ...partial };
      localStorage.setItem('authUser', JSON.stringify(updated));
      return updated;
    });
  }, []);

  // Login
  const login = useCallback(async (request: LoginRequest) => {
    setStatus('loading');
    try {
      const response = await authService.login(request);
      storeAuthData(response);
      setUser(response.user);
      setHousehold(response.household);
      setStatus('authenticated');
    } catch (error) {
      setStatus('unauthenticated');
      throw error;
    }
  }, []);

  // Signup (founder) - returns message, doesn't log in
  const signup = useCallback(async (request: SignupRequest) => {
    const response = await authService.signup(request);
    return response;
  }, []);

  // Signup via invitation - logs in immediately
  const signupInvited = useCallback(async (request: InvitedSignupRequest) => {
    setStatus('loading');
    try {
      const response = await authService.signupInvited(request);
      storeAuthData(response);
      setUser(response.user);
      setHousehold(response.household);
      setStatus('authenticated');
    } catch (error) {
      setStatus('unauthenticated');
      throw error;
    }
  }, []);

  // Logout
  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch {
      // Ignore logout errors - we're clearing local state anyway
    }
    queryClient.clear();
    clearStoredAuth();
    setUser(null);
    setHousehold(null);
    setStatus('unauthenticated');
  }, [queryClient]);

  // Memoize context value to prevent re-render cascade through the component tree
  const value = useMemo<AuthContextValue>(() => {
    const isAuthenticated = status === 'authenticated';
    const isLoading = status === 'loading';
    const isOwner = user?.role === 'owner';
    const userId = user?.id ?? null;
    const householdId = household?.id ?? null;

    return {
      status,
      user,
      household,
      isAuthenticated,
      isLoading,
      isOwner,
      userId,
      householdId,
      login,
      signup,
      signupInvited,
      logout,
      refreshAuth,
      updateUser
    };
  }, [status, user, household, login, signup, signupInvited, logout, refreshAuth, updateUser]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// =============================================================================
// Hook
// =============================================================================

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Hook that requires authentication.
 * Throws if not authenticated (useful in protected routes).
 */
export function useRequireAuth() {
  const auth = useAuth();
  if (!auth.isAuthenticated) {
    throw new Error('Authentication required');
  }
  return auth as AuthContextValue & {
    user: AuthUser;
    household: AuthHousehold;
    userId: string;
    householdId: string;
  };
}
